import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  Loader2, CheckCircle2, XCircle, AlertTriangle, ArrowRight, Heart,
} from "lucide-react";

const brl = (v) =>
  (Number(v || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Landing page for the emailed cancellation link.
 *
 * The user got here from the e-mail we sent when they clicked "Cancel"
 * on the billing screen. Flow:
 *   - Reads ``?token=xxx`` from the URL.
 *   - POSTs to ``/subscription/cancel/confirm`` — the backend validates
 *     the token, fires the Asaas cancellation and returns the updated
 *     plan summary (still ``can_use=true`` during the grace period).
 *   - Renders a warm farewell + confirms the grace period end date.
 *
 * The user must be authenticated (the endpoint requires an account_admin
 * token). Because our JWT lives in an httpOnly cookie, hitting this URL
 * from the same browser Just Works — no manual login needed.
 */
export default function CancelSubscription() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [state, setState] = useState("processing"); // processing | success | error | needs_login
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!token || token.length < 20) {
        setState("error");
        setError("Link inválido — o token está ausente ou malformado.");
        return;
      }
      try {
        const { data } = await api.post("/subscription/cancel/confirm", { token });
        if (cancelled) return;
        setResult(data);
        setState("success");
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        if (status === 401) {
          setState("needs_login");
          return;
        }
        setError(formatApiError(e.response?.data?.detail) || "Não foi possível processar o cancelamento.");
        setState("error");
      }
    }
    run();
    return () => { cancelled = true; };
  }, [token]);

  const paidUntilDate = result?.paid_until
    ? new Date(result.paid_until).toLocaleDateString("pt-BR", {
        day: "2-digit", month: "long", year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="cancel-page">
      <div className="w-full max-w-xl bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
        <div className="px-8 pt-8 pb-4 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Assinatura Vyntric
          </div>
          <h1 className="text-3xl font-black tracking-tighter mt-1">
            {state === "processing" && "Confirmando cancelamento…"}
            {state === "success" && "Assinatura cancelada"}
            {state === "error" && "Não foi possível cancelar"}
            {state === "needs_login" && "Faça login para confirmar"}
          </h1>
        </div>

        <div className="p-8">
          {state === "processing" && (
            <div className="flex items-center gap-3 text-slate-600" data-testid="cancel-processing">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
              Validando o link e enviando o comando ao Asaas…
            </div>
          )}

          {state === "success" && (
            <div className="space-y-5" data-testid="cancel-success">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                <div>
                  <div className="text-lg font-black text-slate-900 leading-snug">
                    Tudo certo. Nenhuma nova cobrança será feita.
                  </div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                    O cancelamento foi registrado no Asaas e sua próxima fatura
                    não será gerada. Você não precisa fazer mais nada.
                  </p>
                </div>
              </div>

              {paidUntilDate && (
                <div className="border-2 border-emerald-200 bg-emerald-50 rounded-lg p-4" data-testid="cancel-grace">
                  <div className="text-[10px] uppercase tracking-widest font-black text-emerald-800">
                    Acesso garantido até
                  </div>
                  <div className="text-2xl font-black tracking-tighter text-emerald-900 mt-1" data-testid="cancel-paid-until">
                    {paidUntilDate}
                  </div>
                  <div className="text-xs text-emerald-800 mt-2 leading-relaxed">
                    A mensalidade{" "}
                    {result?.monthly_total_brl ? <>de <b>{brl(result.monthly_total_brl)}</b>{" "}</> : null}
                    que você já pagou cobre o ciclo atual. Depois dessa data
                    o sistema pausa automaticamente e nenhuma nova cobrança
                    será feita.
                  </div>
                </div>
              )}

              <div className="border border-slate-200 bg-slate-50 rounded-lg p-4 flex items-start gap-3">
                <Heart className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700 leading-relaxed">
                  <b>Obrigado por experimentar o Vyntric.</b> Seu histórico,
                  regras de desconto e integrações ficam preservados — se
                  quiser voltar, basta reativar e continuamos exatamente
                  de onde paramos. Boa jornada! 👋
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Link
                  to="/minha-conta?tab=billing"
                  className="px-4 py-2.5 text-xs font-black uppercase tracking-wider border-2 border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 rounded-sm inline-flex items-center gap-2"
                  data-testid="cancel-goto-billing"
                >
                  Ir para Minha Conta
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <button
                  onClick={() => navigate("/app")}
                  className="px-4 py-2.5 text-xs font-black uppercase tracking-wider border-2 border-slate-200 hover:bg-slate-50 rounded-sm"
                  data-testid="cancel-goto-home"
                >
                  Voltar ao Vyntric
                </button>
              </div>
            </div>
          )}

          {state === "error" && (
            <div className="space-y-4" data-testid="cancel-error">
              <div className="flex items-start gap-3">
                <XCircle className="w-7 h-7 text-red-600 flex-shrink-0" />
                <div>
                  <div className="text-sm font-black text-red-900">Ops.</div>
                  <div className="text-sm text-slate-700 mt-1">{error}</div>
                </div>
              </div>
              <div className="text-xs text-slate-500 leading-relaxed border-t border-slate-100 pt-4">
                Sua assinatura <b>permanece ativa</b>. Se quiser realmente cancelar,
                volte à tela &quot;Minha Conta&quot; e solicite um novo link.
              </div>
              <div className="pt-1">
                <Link
                  to="/minha-conta?tab=billing"
                  className="px-4 py-2.5 text-xs font-black uppercase tracking-wider border-2 border-slate-700 bg-slate-800 text-white hover:bg-slate-900 rounded-sm inline-flex items-center gap-2"
                  data-testid="cancel-error-back"
                >
                  Voltar para Minha Conta
                </Link>
              </div>
            </div>
          )}

          {state === "needs_login" && (
            <div className="space-y-4" data-testid="cancel-needs-login">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-7 h-7 text-amber-600 flex-shrink-0" />
                <div>
                  <div className="text-sm font-black text-amber-900">Sessão expirada</div>
                  <div className="text-sm text-slate-700 mt-1 leading-relaxed">
                    Para confirmar o cancelamento é preciso estar logado.
                    Faça login novamente e clique de novo no botão do e-mail
                    — o link continua válido.
                  </div>
                </div>
              </div>
              <div className="pt-1">
                <Link
                  to={`/login?next=/cancel-subscription?token=${encodeURIComponent(token)}`}
                  className="px-4 py-2.5 text-xs font-black uppercase tracking-wider border-2 border-slate-700 bg-slate-800 text-white hover:bg-slate-900 rounded-sm inline-flex items-center gap-2"
                  data-testid="cancel-goto-login"
                >
                  Ir para login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
